import React, { useState, useEffect, useRef } from "react";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import "../styles/dashboard.css";
import {
  RouteItem,
  MenuSection,
  MenuData,
  OpenMenus,
  PopupPosition,
} from "../models/Dashboard/types";
import {
  getMainMenuIcon,
  getSubmenuIcon,
  getListItemIcon,
  DashboardIcon,
  ChevronDownIcon,
  MenuIcon,
  ChevronLeftIcon,
  ChevronRightIcon
} from "../view/common/dashboard/dashboardIcons";
import { useSelector } from "react-redux";
import { RootState } from "../redux/slices/store";

const Dashboard: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const permissions = useSelector((state: RootState) => state.permissions);

  const hasReadPermission = (menuName: string): boolean => {
    const permissionMappings: Record<string, string> = {
      'newItem': 'items',
      'itemCategory': 'itemcategorys',
      'itemModel': 'itemmodels',
      'Inward': 'inwardoptions',
      'newRack': 'racks',
      'Outward': 'outwardoptions',
      'itemAssignedRack': 'itemassignedracks',
      'bomtypes': 'bomtypes',
      'newContributor': 'contributors',
      'customerParts': 'customerparts',
      'supplierParts': 'supplierparts',
      'cityMaster': 'citymasters',
      'stateMaster': 'statemaster',
      'countryMaster': 'country-masters',
      'currencyMaster': 'currencymaster',
      'gstSlabMaster': 'gstslabs',
      'uomMaster': 'uoms',
      'approvalRole': 'roles',
      'newGRN': 'grns',
      'grnSummary': 'grns',
      'grnReport': 'grns',
      'incobar': 'barcode',
      'CurrentStock': 'stocks',
      'stocksummary': 'stocksummary',
       'users':'users',
       'roles':'roles',
      'purchaseorder': 'purchase-orders',
      'pendingpo':'purchase-orders',
      'stockexpire':'stocks',
      'StockAged':"stocks",
      'pricehistory':'stocks',
      'RMinventory':"rm-inventory-cost",
      'todaystocks':"stocks",
      'bom':'bom'
    };

    const permissionKey = permissionMappings[menuName] || menuName;
    const hasPermission = permissions?.menus?.[permissionKey]?.read === true;

    return hasPermission;
  };

  const filterRoutesByPermission = (routes: Array<{ path: string, label: string }>) => {
    return routes.filter(route => hasReadPermission(route.path));
  };

  const baseMenuData: MenuData = {
    MasterItem: {
      head: "Master",
      routes: [],
      submenus: {
        NewItem: {
          head: "New Item",
          routes: [
            { path: "newItem", label: "New Item" },
            { path: "itemCategory", label: "Item Category" },
            { path: "itemModel", label: "Item Model" },
            { path: "newRack", label: "Rack" },
            { path: "itemAssignedRack", label: "Item Assigned Rack" },
            { path: "bomtypes", label: "BOM Types" },
          ],
        },
        Contributor: {
          head: "Contributor",
          routes: [
            { path: "newContributor", label: "New Contributor" },
            { path: "customerParts", label: "Customer Parts" },
            { path: "supplierParts", label: "Supplier Parts" },
            { path: "deliveryPlace", label: "Delivery Place" },
          ],
        },
        General: {
          head: "General",
          routes: [
            { path: "cityMaster", label: "City Master" },
            { path: "stateMaster", label: "State Master" },
            { path: "countryMaster", label: "Country Master" },
            { path: "currencyMaster", label: "Currency Master" },
            { path: "gstSlabMaster", label: "GST Slab Master" },
            { path: "uomMaster", label: "UOM Master" },
            { path: "approvalRole", label: "Approval Role" },
            {path:"users", label:"users"},
            {path:"roles",label:"roles"}
          ],
        },
      },
    },
    Purchase: {
      head: "Purchase",
      routes: [],
      submenus: {
        GRN: {
          head: "GRN",
          routes: [
            { path: "newGRN", label: "New GRN" },
            { path: "grnSummary", label: "GRN Summary" },
            { path: "grnReport", label: "GRN Report" },
          ],
        },
        PurchaseOrder: {
          head: "PurchaseOrder",
          routes: [
            { path: "purchaseorder", label: "Purchase Order" },
            { path: "pendingpo", label:"Pending Po"},
            { path: "pricehistory", label: "Price History" },
            {path:"RMinventory" ,label:"RM inventory"}
          ],
        },
        Bom: {
          head:"Bom",
          routes: [
            {path: "bom", label: "Bom"}
          ]
        }
      },
    },
    GRN: {
      head: "Inventory",
      routes: [],
      submenus: {
        PartTag: {
          head: "Part Tag",
          routes: [{ path: "incobar", label: "IncoBarTag" }],
        },
        Scanning: {
          head: "Scanning",
          routes: [
            { path: "Inward", label: "Inward" },
            { path: "Outward", label: "Outward" },
          ],
        },
        Stocks: {
          head: "Stocks",
          routes: [
            { path: "CurrentStock", label: "Current Stock" },
            { path: "stocksummary", label: "Stock Summary" },
            { path: "todaystocks", label: "Today Stocks" },
            {path:"stockexpire",label:"stock expiry"},
            {path:"StockAged",label:"Stock Aged"}
          ],
        },
      },
    },
  };

  const processMenuData = (): MenuData => {
    const result: MenuData = JSON.parse(JSON.stringify(baseMenuData));

    Object.keys(result).forEach(menuKey => {
      const menuItem = result[menuKey];

      if (menuItem.routes) {
        menuItem.routes = filterRoutesByPermission(menuItem.routes);
      }
      
      if (menuItem.submenus) {
        Object.keys(menuItem.submenus).forEach(submenuKey => {
          const submenu = menuItem.submenus?.[submenuKey];
          if (submenu?.routes) {
            submenu.routes = filterRoutesByPermission(submenu.routes);
          }
        });

        Object.keys(menuItem.submenus).forEach(submenuKey => {
          if (menuItem.submenus?.[submenuKey].routes.length === 0) {
            delete menuItem.submenus[submenuKey];
          }
        });
      }
    });

    return result;
  };

  const menuData = processMenuData();

  const initializeMenuState = (): OpenMenus => {
    return {
      MasterItem: false,
      MasterItemContributor: false,
      MasterItemGeneral: false,
      MasterItemNewItem: false,
      Purchase: false,
      GRN: false,
      GrnReport: false,
      GRNPartTag: false,
      GRNScanning: false,
      GRNStocks: false,
    };
  };

  const [openMenus, setOpenMenus] = useState<OpenMenus>(initializeMenuState());
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(() => {
    const savedState = localStorage.getItem("isSidebarCollapsed");
    return savedState ? JSON.parse(savedState) : false;
  });
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activePopup, setActivePopup] = useState<keyof OpenMenus | null>(null);
  const [popupPosition, setPopupPosition] = useState<PopupPosition>({
    top: 0,
    left: 0,
  });
  const [hoveredMenu, setHoveredMenu] = useState<keyof OpenMenus | null>(null);
  const [popupVisibilityTimer, setPopupVisibilityTimer] = useState<NodeJS.Timeout | null>(null);

  const popupRef = useRef<HTMLDivElement>(null);
  const menuRefs = useRef<{ [key: string]: HTMLElement | null }>({});

  useEffect(() => {
    setOpenMenus(initializeMenuState());
  }, []);

  useEffect(() => {
    localStorage.setItem("openMenus", JSON.stringify(openMenus));
  }, [openMenus]);

  useEffect(() => {
    localStorage.setItem(
      "isSidebarCollapsed",
      JSON.stringify(isSidebarCollapsed)
    );
  }, [isSidebarCollapsed]);

  useEffect(() => {
    if (location.pathname) {
      const path = location.pathname.split("/").pop() || "";

      const openParentMenus = () => {
        for (const [menuKey, menuItem] of Object.entries(menuData)) {
          if (menuItem.routes.some((route: RouteItem) => route.path === path)) {
            setOpenMenus((prev) => ({ ...prev, [menuKey]: true }));
            return;
          }

          if (menuItem.submenus) {
            for (const [submenuKey, submenu] of Object.entries(
              menuItem.submenus
            )) {
              if (
                submenu.routes.some((route: RouteItem) => route.path === path)
              ) {
                const parentKey = menuKey;
                const submenuStateKey =
                  `${menuKey}${submenuKey}` as keyof OpenMenus;

                setOpenMenus((prev) => ({
                  ...prev,
                  [parentKey]: true,
                  [submenuStateKey]: true,
                }));
                return;
              }
            }
          }
        }
      };

      openParentMenus();
    }
  }, [location.pathname]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        popupRef.current &&
        !popupRef.current.contains(event.target as Node) &&
        !Object.keys(menuRefs.current).some(key =>
          menuRefs.current[key] && menuRefs.current[key]!.contains(event.target as Node)
        )
      ) {
        setActivePopup(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
    setActivePopup(null);
  }, [location.pathname]);

  const calculatePopupPosition = (element: HTMLElement): PopupPosition => {
    const rect = element.getBoundingClientRect();
    const windowHeight = window.innerHeight;
    const windowWidth = window.innerWidth;

    if (isSidebarCollapsed) {
      let left = rect.right + 10;
      if (left + 220 > windowWidth) {
        left = Math.max(10, rect.left - 230);
      }

      let top = rect.top;
      if (top + 400 > windowHeight) {
        top = Math.max(10, windowHeight - 420);
      }

      return { top, left };
    }
    else {
      let left = rect.right + 5;
      if (left + 220 > windowWidth) {
        left = Math.max(10, rect.left - 230);
      }

      return { top: rect.top, left };
    }
  };

  const toggleMenu = (menu: keyof OpenMenus, event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();

    const targetElement = event.currentTarget as HTMLElement;
    menuRefs.current[menu] = targetElement;

    if (isSidebarCollapsed) {
      const newPosition = calculatePopupPosition(targetElement);
      setPopupPosition(newPosition);
      setActivePopup(activePopup === menu ? null : menu);
    } else {
      setOpenMenus((prev) => ({
        ...prev,
        [menu]: !prev[menu],
      }));
    }
  };

  const toggleSidebar = () => {
    setIsSidebarCollapsed(!isSidebarCollapsed);
    setActivePopup(null);
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const isActive = (path: string) => {
    return location.pathname.includes(path);
  };

  const handlePopupItemClick = (path: string, event: React.MouseEvent) => {
    event.stopPropagation();
    navigate(`/dashboard/${path.replace(/^\//, "")}`);
    setActivePopup(null);
  };

  const handleMouseEnter = (menu: keyof OpenMenus, event: React.MouseEvent) => {
    if (popupVisibilityTimer) {
      clearTimeout(popupVisibilityTimer);
      setPopupVisibilityTimer(null);
    }

    setHoveredMenu(menu);

    if (isSidebarCollapsed) {
      const targetElement = event.currentTarget as HTMLElement;
      menuRefs.current[menu] = targetElement;

      const newPosition = calculatePopupPosition(targetElement);
      setPopupPosition(newPosition);
      setActivePopup(menu);
    }
  };

  const handleMouseLeave = () => {
    setHoveredMenu(null);
    if (isSidebarCollapsed) {
      const timer = setTimeout(() => {
        if (!popupRef.current?.matches(':hover')) {
          setActivePopup(null);
        }
      }, 300);

      setPopupVisibilityTimer(timer);
    }
  };
  const setMenuRef = (key: string) => (el: HTMLElement | null) => {
    menuRefs.current[key] = el;
  };

  const renderPopupMenu = (menuKey: keyof OpenMenus) => {
    if (
      menuKey === "MasterItemNewItem" &&
      menuData.MasterItem?.submenus?.NewItem
    ) {
      return (
        <div
          ref={popupRef}
          className="popup-menu"
          style={{
            position: "fixed",
            top: `${popupPosition.top}px`,
            left: `${popupPosition.left}px`,
            zIndex: 1050,
          }}
          onClick={(e) => e.stopPropagation()}
          onMouseEnter={() => {
            if (popupVisibilityTimer) {
              clearTimeout(popupVisibilityTimer);
              setPopupVisibilityTimer(null);
            }
          }}
          onMouseLeave={() => {
            const timer = setTimeout(() => setActivePopup(null), 300);
            setPopupVisibilityTimer(timer);
          }}
        >
          <h3>{menuData.MasterItem.submenus.NewItem.head}</h3>
          <ul>
            {menuData.MasterItem.submenus.NewItem.routes.map(
              ({ path, label }) => (
                <li key={path}>
                  <button
                    className={`popup-item ${isActive(path) ? "active" : ""}`}
                    onClick={(e) => handlePopupItemClick(path, e)}
                  >
                    {getListItemIcon("MasterItem", "NewItem", path)}
                    <span className="popup-item-text">{label}</span>
                  </button>
                </li>
              )
            )}
          </ul>
        </div>
      );
    }

    if (
      menuKey === "MasterItemContributor" &&
      menuData.MasterItem?.submenus?.Contributor
    ) {
      return (
        <div
          ref={popupRef}
          className="popup-menu"
          style={{
            position: "fixed",
            top: `${popupPosition.top}px`,
            left: `${popupPosition.left}px`,
            zIndex: 1050,
          }}
          onClick={(e) => e.stopPropagation()}
          onMouseEnter={() => {
            if (popupVisibilityTimer) {
              clearTimeout(popupVisibilityTimer);
              setPopupVisibilityTimer(null);
            }
          }}
          onMouseLeave={() => {
            const timer = setTimeout(() => setActivePopup(null), 300);
            setPopupVisibilityTimer(timer);
          }}
        >
          <h3>{menuData.MasterItem.submenus.Contributor.head}</h3>
          <ul>
            {menuData.MasterItem.submenus.Contributor.routes.map(
              ({ path, label }) => (
                <li key={path}>
                  <button
                    className={`popup-item ${isActive(path) ? "active" : ""}`}
                    onClick={(e) => handlePopupItemClick(path, e)}
                  >
                    {getListItemIcon("MasterItem", "Contributor", path)}
                    <span className="popup-item-text">{label}</span>
                  </button>
                </li>
              )
            )}
          </ul>
        </div>
      );
    }

    if (
      menuKey === "MasterItemGeneral" &&
      menuData.MasterItem?.submenus?.General
    ) {
      return (
        <div
          ref={popupRef}
          className="popup-menu"
          style={{
            position: "fixed",
            top: `${popupPosition.top}px`,
            left: `${popupPosition.left}px`,
            zIndex: 1050,
          }}
          onClick={(e) => e.stopPropagation()}
          onMouseEnter={() => {
            if (popupVisibilityTimer) {
              clearTimeout(popupVisibilityTimer);
              setPopupVisibilityTimer(null);
            }
          }}
          onMouseLeave={() => {
            const timer = setTimeout(() => setActivePopup(null), 300);
            setPopupVisibilityTimer(timer);
          }}
        >
          <h3>{menuData.MasterItem.submenus.General.head}</h3>
          <ul>
            {menuData.MasterItem.submenus.General.routes.map(
              ({ path, label }) => (
                <li key={path}>
                  <button
                    className={`popup-item ${isActive(path) ? "active" : ""}`}
                    onClick={(e) => handlePopupItemClick(path, e)}
                  >
                    {getListItemIcon("MasterItem", "General", path)}
                    <span className="popup-item-text">{label}</span>
                  </button>
                </li>
              )
            )}
          </ul>
        </div>
      );
    }

    const menu = menuData[menuKey as keyof MenuData];
    if (!menu) return null;

    return (
      <div
        ref={popupRef}
        className="popup-menu"
        style={{
          position: "fixed",
          top: `${popupPosition.top}px`,
          left: `${popupPosition.left}px`,
          zIndex: 1050,
        }}
        onClick={(e) => e.stopPropagation()}
        onMouseEnter={() => {
          if (popupVisibilityTimer) {
            clearTimeout(popupVisibilityTimer);
            setPopupVisibilityTimer(null);
          }
        }}
        onMouseLeave={() => {
          const timer = setTimeout(() => setActivePopup(null), 300);
          setPopupVisibilityTimer(timer);
        }}
      >
        <h3>{menu.head}</h3>
        <ul>
          {menu.routes && menu.routes.length > 0 && (
            <>
              {menu.routes.map(({ path, label }) => (
                <li key={path}>
                  <button
                    className={`popup-item ${isActive(path) ? "active" : ""}`}
                    onClick={(e) => handlePopupItemClick(path, e)}
                  >
                    <span className="popup-item-text">{label}</span>
                  </button>
                </li>
              ))}
            </>
          )}

          {menu.submenus &&
            Object.keys(menu.submenus).map((submenuKey) => {
              const submenu = menu.submenus![submenuKey];
              return (
                <li key={submenuKey} className="submenu-section">
                  <div className="submenu-header">
                    {getSubmenuIcon(menuKey as string, submenuKey)}
                    <span>{submenu.head}</span>
                  </div>
                  <ul className="submenu-items">
                    {submenu.routes.map(({ path, label }) => (
                      <li key={path}>
                        <button
                          className={`popup-item ${isActive(path) ? "active" : ""
                            }`}
                          onClick={(e) => handlePopupItemClick(path, e)}
                        >
                          {getListItemIcon(
                            menuKey as string,
                            submenuKey,
                            path
                          )}
                          <span className="popup-item-text">{label}</span>
                        </button>
                      </li>
                    ))}
                  </ul>
                </li>
              );
            })}
        </ul>
      </div>
    );
  };

  const renderSubmenuItems = (
    menuKey: keyof MenuData,
    isCollapsed: boolean
  ) => {
    const menu = menuData[menuKey];
    if (!menu) return null;

    const hasContent = menu.routes.length > 0 ||
      (menu.submenus ? Object.values(menu.submenus).some(submenu => submenu.routes.length > 0) : false);

    if (!hasContent) return null;

    return (
      <>
        <span
          className={`menu-label ${hoveredMenu === menuKey ? "hovered" : ""}`}
          onClick={(e) => toggleMenu(menuKey as keyof OpenMenus, e)}
          title={isCollapsed ? menu.head : ""}
          onMouseEnter={(e) => handleMouseEnter(menuKey as keyof OpenMenus, e)}
          onMouseLeave={handleMouseLeave}
          ref={setMenuRef(menuKey as string)}
        >
          {getMainMenuIcon(menuKey as string)}
          {!isCollapsed && (
            <>
              <div>{menu.head}</div>
              <ChevronDownIcon
                className={`dropdown-icon ${openMenus[menuKey as keyof OpenMenus] ? "rotate-180" : ""
                  }`}
              />
            </>
          )}
        </span>

        {isCollapsed &&
          activePopup === menuKey &&
          renderPopupMenu(menuKey as keyof OpenMenus)}

        {openMenus[menuKey as keyof OpenMenus] && !isCollapsed && (
          <ul className="submenu-list open">
            {menu.routes && menu.routes.length > 0 && (
              <ul className="nested-submenu-list">
                {menu.routes.map(({ path, label }) => (
                  <li key={path}>
                    <Link
                      to={`/dashboard/${path}`}
                      className={`submenu-item no-underline ${isActive(path) ? "active" : ""
                        }`}
                    >
                      {label}
                    </Link>
                  </li>
                ))}
              </ul>
            )}

            {menu.submenus &&
              Object.keys(menu.submenus).map((submenuKey) => {
                const submenu = menu.submenus![submenuKey];
                const submenuStateKey =
                  `${menuKey}${submenuKey}` as keyof OpenMenus;

                if (submenu.routes.length === 0) return null;

                return (
                  <li key={submenuStateKey} className="submenu">
                    <span
                      className={`menu-label ${hoveredMenu === submenuStateKey ? "hovered" : ""
                        }`}
                      onClick={(e) => toggleMenu(submenuStateKey, e)}
                      title={isCollapsed ? submenu.head : ""}
                      onMouseEnter={(e) => handleMouseEnter(submenuStateKey, e)}
                      onMouseLeave={handleMouseLeave}
                      ref={setMenuRef(submenuStateKey)}
                    >
                      {getSubmenuIcon(menuKey as string, submenuKey)}

                      {!isCollapsed && (
                        <>
                          <div>{submenu.head}</div>
                          <ChevronDownIcon
                            className={`dropdown-icon ${openMenus[submenuStateKey] ? "rotate-180" : ""
                              }`}
                          />
                        </>
                      )}
                    </span>

                    {isCollapsed &&
                      activePopup === submenuStateKey &&
                      renderPopupMenu(submenuStateKey)}

                    {openMenus[submenuStateKey] && !isCollapsed && (
                      <ul className="nested-submenu-list">
                        {submenu.routes.map(({ path, label }) => (
                          <li key={path}>
                            <Link
                              to={`/dashboard/${path}`}
                              className={`submenu-item no-underline ${isActive(path) ? "active" : ""
                                }`}
                            >
                              {getListItemIcon(
                                menuKey as string,
                                submenuKey,
                                path
                              )}
                              {label}
                            </Link>
                          </li>
                        ))}
                      </ul>
                    )}
                  </li>
                );
              })}
          </ul>
        )}
      </>
    );
  };

  useEffect(() => {
    return () => {
      if (popupVisibilityTimer) {
        clearTimeout(popupVisibilityTimer);
      }
    };
  }, [popupVisibilityTimer]);

  return (
    <div className="dashboard">
      <button
        className={`mobile-menu-toggle ${isMobileMenuOpen ? "active" : ""}`}
        onClick={toggleMobileMenu}
        aria-label="Toggle mobile menu"
      >
        <MenuIcon size={24} />
      </button>

      <aside
        className={`sidebar ${isSidebarCollapsed ? "collapsed" : ""} ${isMobileMenuOpen ? "mobile-open" : ""
          }`}
      >
        <nav>
          <ul>
            <li className={`submenu ${isActive("/") ? "active" : ""}`}>
              <Link
                to="/dashboard"
                className="menu-label no-underline"
                title={isSidebarCollapsed ? "Dashboard" : ""}
              >
                <DashboardIcon className="menu-icon" />
                {!isSidebarCollapsed && <div>Dashboard</div>}
              </Link>
            </li>

            <li className="submenu">
              {renderSubmenuItems("MasterItem", isSidebarCollapsed)}
            </li>

            <li className="submenu">
              {renderSubmenuItems("Purchase", isSidebarCollapsed)}
            </li>

            <li className="submenu">
              {renderSubmenuItems("GRN", isSidebarCollapsed)}
            </li>

            <li className="submenu sidebar-close-container">
              <button
                className="sidebar-toggle-btn"
                onClick={toggleSidebar}
                title={
                  isSidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"
                }
              >
                {isSidebarCollapsed ? (
                  <ChevronRightIcon size={15} />
                ) : (
                  <ChevronLeftIcon size={15} />
                )}
              </button>
            </li>
          </ul>
        </nav>
      </aside>

      <main className={`content ${isSidebarCollapsed ? "expanded" : ""}`}>
        <Outlet />
      </main>

      {isMobileMenuOpen && (
        <div
          className="sidebar-overlay"
          onClick={() => setIsMobileMenuOpen(false)}
        ></div>
      )}
    </div>
  );
};

export default Dashboard;